/**
 * Site sensors slice — per-site FE-contract sensor readings, seeded by the
 * dashboard bootstrap thunk and kept live by socket events.
 *
 * Stores the server's sensor contract verbatim keyed by siteId. REST seeds with
 * pre-converted temperature/humidity values; socket deltas carry raw values and
 * are converted client-side (the broadcast has no per-user context).
 *
 * Contains:
 * - siteSensors slice with bySiteId state
 * - selectSiteSensors / selectSiteSensorsStatus selectors
 * - handleSiteSensorsSocketActions socket handler
 */
import { createSlice } from '@reduxjs/toolkit';

import { fetchSiteSensors } from '@sophocles/features/siteSensors/thunk';
import { displayRh, rawToC, rawToF } from '@sophocles/utils/sensorConversions';

/**
 * Convert a raw sensor reading to a display value for the live socket path.
 *
 * REST ships value pre-converted; the socket broadcast has no per-user unit
 * context, so it ships raw + unit and the client converts here.
 *
 * @param {number|null} raw  - 16-bit raw integer
 * @param {string|null} unit - '°F', '°C', or '%'
 * @returns {number|null}
 */
function rawToDisplayValue(raw, unit) {
  if (raw == null) return null;
  if (unit === '°C') return Math.round(rawToC(raw));
  if (unit === '°F') return Math.round(rawToF(raw));
  if (unit === '%') return displayRh(raw);
  return null;
}

/**
 * Apply the raw→display conversion to a single sensor's temperature and
 * humidity fields, returning a new sensor object (never mutates).
 *
 * @param {object} sensor - sensor row from the socket payload
 * @returns {object}
 */
function applySocketConversions(sensor) {
  const temp = sensor.temperature ?? {};
  const humidity = sensor.humidity ?? {};
  return {
    ...sensor,
    temperature: {
      ...temp,
      value: rawToDisplayValue(temp.raw ?? null, temp.unit ?? null),
    },
    humidity: {
      ...humidity,
      value: rawToDisplayValue(humidity.raw ?? null, humidity.unit ?? null),
    },
  };
}

const slice = createSlice({
  name: 'siteSensors',
  initialState: {
    /**
     * Per-site sensor readings snapshot keyed by String(siteId).
     *
     * Shape per entry:
     *   { loading, error, sensors, isOnline, lastReceiveTime, generatedAt, relayControllerId }
     *
     * @type {{ [siteId: string]: { loading: boolean, error: string|null, sensors: object[], isOnline: boolean|null, lastReceiveTime: string|null, generatedAt: string|null, relayControllerId: number|null } }}
     */
    bySiteId: {},
  },
  reducers: {
    // Applied by the socket handler after pre-seed + monotonic guards pass.
    _applySocketPatch(state, action) {
      const { siteId, sensors, isOnline, lastReceiveTime, generatedAt, relayControllerId } = action.payload;
      const prev = state.bySiteId[siteId] ?? {};
      state.bySiteId[siteId] = {
        ...prev,
        sensors,
        isOnline,
        lastReceiveTime,
        generatedAt,
        relayControllerId,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSiteSensors.pending, (state, action) => {
        const siteId = String(action.meta.arg);
        const prev = state.bySiteId[siteId] ?? {};
        state.bySiteId[siteId] = { ...prev, loading: true, error: null };
      })
      .addCase(fetchSiteSensors.fulfilled, (state, action) => {
        const payload = action.payload;
        const siteId = String(payload.site_id);
        const prev = state.bySiteId[siteId] ?? {};

        // Monotonic guard: don't overwrite a newer generatedAt with an older one.
        if (prev.generatedAt != null && payload.generated_at != null) {
          if (new Date(payload.generated_at) < new Date(prev.generatedAt)) {
            state.bySiteId[siteId] = { ...prev, loading: false, error: null };
            return;
          }
        }

        state.bySiteId[siteId] = {
          loading: false,
          error: null,
          sensors: payload.sensors ?? [],
          isOnline: payload.is_online ?? null,
          lastReceiveTime: payload.last_receive_time ?? null,
          generatedAt: payload.generated_at ?? null,
          relayControllerId: payload.relay_controller_id ?? null,
        };
      })
      .addCase(fetchSiteSensors.rejected, (state, action) => {
        const siteId = String(action.meta.arg);
        const prev = state.bySiteId[siteId] ?? {};
        state.bySiteId[siteId] = {
          ...prev,
          loading: false,
          error: action.payload ?? 'Failed to load sensor data',
        };
      });
  },
});

/**
 * Select the sensor readings for a site, or null when not yet loaded.
 *
 * @param {string|number} siteId
 * @returns {function} selector returning the entry from bySiteId or null
 */
export const selectSiteSensors = siteId => state => state.siteSensors.bySiteId[String(siteId)] ?? null;

/**
 * Select the loading/error status for a site's sensor data.
 *
 * @param {string|number} siteId
 * @returns {function} selector returning { loading, error }
 */
export const selectSiteSensorsStatus = siteId => state => {
  const entry = state.siteSensors.bySiteId[String(siteId)];
  if (entry == null) return { loading: false, error: null };
  return { loading: entry.loading, error: entry.error };
};

/**
 * Socket handler for the 'site_sensors' model event.
 *
 * Pre-seed no-op guard: if the REST seed hasn't landed yet (bySiteId has no
 * entry for this site), the socket delta is dropped — the card will receive
 * fresh data from the REST fetch that's in-flight.
 *
 * Monotonic guard: a socket payload older than the current generatedAt is
 * dropped so a slow REST seed can't overwrite a fresh socket patch.
 *
 * Raw→display conversion: socket payloads carry raw values (no per-user unit
 * context in the broadcast); temperature and humidity .value fields are
 * computed here from raw + unit.
 *
 * Signature matches getModelHandler outputs: (op, data) => dispatch => void.
 *
 * @param {'+' | '-'} op
 * @param {object} payload - same shape as the REST contract body
 */
export const handleSiteSensorsSocketActions = (op, payload) => (dispatch, getState) => {
  if (!payload || payload.site_id == null) return;

  const siteId = String(payload.site_id);
  const existing = getState().siteSensors.bySiteId[siteId];

  // Pre-seed no-op guard: drop events for sites not yet bootstrapped.
  if (existing == null) return;

  // Monotonic guard: don't overwrite a newer snapshot with an older one.
  if (existing.generatedAt != null && payload.generated_at != null) {
    if (new Date(payload.generated_at) < new Date(existing.generatedAt)) return;
  }

  // Convert raw sensor values to display values (socket has no per-user context).
  const convertedSensors = (payload.sensors ?? []).map(applySocketConversions);

  dispatch(
    slice.actions._applySocketPatch({
      siteId,
      sensors: convertedSensors,
      isOnline: payload.is_online ?? null,
      lastReceiveTime: payload.last_receive_time ?? null,
      generatedAt: payload.generated_at ?? null,
      relayControllerId: payload.relay_controller_id ?? null,
    }),
  );
};

const { reducer } = slice;
export default reducer;
